import ConfirmAction from '@/components/confirm-action';
import PageHeader from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import InvoiceStatusPill from '@/pages/apps/components/invoice-status-pill';
import AttachmentsField from '@/pages/apps/records/components/attachments-field';
import RecordForm, { type FieldError } from '@/pages/apps/records/components/record-form';
import { app_invoice_path, app_list_record_path, new_app_invoice_path } from '@/rails/routes';
import type { AppList, AppRecord, AppShell, Customer } from '@/types/platform';
import { Link, router } from '@inertiajs/react';

// A record on its edit page has been saved, so it always has an id.
interface SavedRecord extends AppRecord {
  id: string;
}

interface RecordsEditProps {
  app: AppShell;
  list: AppList;
  record: SavedRecord;
  customers: Customer[];
  errors: string[];
  fieldErrors: FieldError[];
}

const RecordsEdit = ({ app, list, record, customers, errors, fieldErrors }: RecordsEditProps) => {
  const singular = list.singular.toLowerCase();
  const attachmentFields = list.fields.filter((field) => field.type === 'attachments');
  const showInvoices = list.billable || record.invoices.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={record.title}
        description={list.singular}
        actions={
          <ConfirmAction
            title={`Remove this ${singular}?`}
            description={`This removes the ${singular} and its attachments for good. Invoices already made from it are kept.`}
            confirmLabel="Remove"
            onConfirm={() => router.delete(app_list_record_path(app.id, list.key, record.id))}
          >
            <Button type="button" variant="outline">
              Remove {singular}
            </Button>
          </ConfirmAction>
        }
      />

      {/* Remounts with the stored values after a save. A failed save keeps what was typed. */}
      <RecordForm
        key={record.updatedAt ?? record.id}
        app={app}
        list={list}
        record={record}
        customers={customers}
        errors={errors}
        fieldErrors={fieldErrors}
      />

      {attachmentFields.map((field) => (
        <AttachmentsField
          key={field.key}
          appId={app.id}
          provisioned={app.provisioned}
          recordId={record.id}
          field={field}
          attachments={record.attachments.filter((attachment) => attachment.fieldKey === field.key)}
        />
      ))}

      {showInvoices ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>Invoices</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {record.invoices.length === 0 ? (
              <p className="text-muted-foreground text-sm">No invoice has been made from this.</p>
            ) : (
              <ul className="divide-y rounded-md border">
                {record.invoices.map((invoice) => (
                  <li
                    key={invoice.id}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <Link
                      href={app_invoice_path(app.id, invoice.id)}
                      className="text-sm font-medium underline"
                    >
                      {invoice.number ?? 'Draft'}
                    </Link>
                    <InvoiceStatusPill status={invoice.status} />
                  </li>
                ))}
              </ul>
            )}

            {record.billable ? (
              <Button asChild variant="outline">
                <Link href={new_app_invoice_path(app.id, { record_id: record.id })}>
                  Create invoice
                </Link>
              </Button>
            ) : null}

            {list.billable && !record.billable ? (
              <p className="text-muted-foreground text-sm">
                To invoice this {singular}, pick a customer, enter an amount, and save.
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

export default RecordsEdit;
